import React, { useState, useEffect, useReducer, useRef } from "react";

import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  CircularProgress,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  InsertDriveFileOutlined,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { formatMontevideoDate, formatMontevideoTime, isSameMontevideoDay } from "../../utils/dateTime";
import { useChatAppearance } from "../../context/ChatAppearance";
import { chatCssVariables, chatSurfaceStyle, chatTextColor } from "../../utils/chatAppearance";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
  },

  messagesList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
    padding: "16px clamp(20px, 3vw, 42px) 20px",
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 16px 18px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 12,
    marginTop: 3,
    minWidth: 80,
    width: "fit-content",
    maxWidth: "min(80%, 680px)",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "var(--chat-incoming, #fff)",
    color: "var(--chat-incoming-text, #111b21)",
    alignSelf: "flex-start",
    borderRadius: "0 8px 8px 8px",
    paddingLeft: 7,
    paddingRight: 7,
    paddingTop: 7,
    paddingBottom: 0,
    border: 0,
    boxShadow: "0 1px 2px rgba(11,20,26,.18)",
    "&::before": { content: '\"\"', position: "absolute", top: 0, left: -8, borderTop: "8px solid var(--chat-incoming, #fff)", borderLeft: "8px solid transparent" },
    [theme.breakpoints.down("sm")]: { maxWidth: "86%" },
  },

  quotedContainerLeft: {
    margin: "-3px -3px 7px -3px",
    overflow: "hidden",
    backgroundColor: "rgba(0,0,0,.07)",
    borderRadius: 5,
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 420,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#69b7f2",
  },

  messageRight: {
    marginLeft: 12,
    marginTop: 3,
    minWidth: 80,
    width: "fit-content",
    maxWidth: "min(80%, 680px)",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "var(--chat-outgoing, #d9fdd3)",
    color: "var(--chat-outgoing-text, #111b21)",
    alignSelf: "flex-end",
    borderRadius: "8px 0 8px 8px",
    paddingLeft: 7,
    paddingRight: 7,
    paddingTop: 7,
    paddingBottom: 0,
    border: 0,
    boxShadow: "0 1px 2px rgba(11,20,26,.18)",
    "&::before": { content: '\"\"', position: "absolute", top: 0, right: -8, borderTop: "8px solid var(--chat-outgoing, #d9fdd3)", borderRight: "8px solid transparent" },
    [theme.breakpoints.down("sm")]: { maxWidth: "86%" },
  },
  messageGrouped: { borderRadius: 8, "&::before": { display: "none" } },

  quotedContainerRight: {
    margin: "-3px -3px 7px -3px",
    overflowY: "hidden",
    backgroundColor: "rgba(0,0,0,.08)",
    borderRadius: 5,
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 420,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.primary.main,
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "inherit",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "anywhere",
    padding: "3px 3px 20px",
    lineHeight: 1.42,
    fontSize: ".9rem",
    "& a": { color: "#1697c8", textDecoration: "underline" },
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    opacity: .65,
    overflowWrap: "break-word",
    padding: "3px 3px 20px",
  },

  messageMedia: {
    objectFit: "cover",
    width: "min(360px, 100%)",
    maxHeight: 340,
    height: "auto",
    borderRadius: 12,
    display: "block",
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    right: 8,
    bottom: 4,
    color: "inherit",
    opacity: .65,
    display: "inline-flex",
    alignItems: "center",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "auto",
    backgroundColor: "var(--chat-date-bg, #fff)",
    margin: "8px 0 14px",
    borderRadius: 7,
    border: 0,
    boxShadow: "0 1px 2px rgba(11,20,26,.12)",
  },

  dailyTimestampText: {
    color: "var(--chat-date-text, #52615b)",
    fontSize: ".72rem",
    padding: "5px 10px",
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: "#53bdeb",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "inherit",
    padding: 4,
  },
  documentCard: {
    minWidth: "min(240px, 100%)",
    maxWidth: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 10px 10px 12px",
    borderRadius: 11,
    color: "inherit",
    textDecoration: "none",
    backgroundColor: "rgba(0,0,0,.06)",
    border: "1px solid #8883",
    "&:hover": { backgroundColor: "rgba(0,0,0,.1)" },
  },
  documentIcon: { color: "#ef4f5a", fontSize: 32 },
  documentMeta: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" },
  documentName: { fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 },
  documentSize: { fontSize: ".72rem", color: "inherit", opacity: .7 },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];
    let changed = false;
    const nextState = [...state];

    messages.forEach((message) => {
      const messageIndex = nextState.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        const current = nextState[messageIndex];
        const isSame = current.body === message.body
          && current.read === message.read
          && current.ack === message.ack
          && current.isDeleted === message.isDeleted
          && current.mediaType === message.mediaType
          && current.mediaUrl === message.mediaUrl
          && current.fileSize === message.fileSize;
        if (!isSame) {
          nextState[messageIndex] = message;
          changed = true;
        }
      } else {
        newMessages.push(message);
        changed = true;
      }
    });

    return changed ? [...newMessages, ...nextState].sort((left, right) => new Date(left.createdAt) - new Date(right.createdAt)) : state;
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state].sort((left, right) => new Date(left.createdAt) - new Date(right.createdAt));
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup }) => {
  const classes = useStyles();
  const { appearance } = useChatAppearance();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const lastMessageRef = useRef();
  const messagesScrollRef = useRef();
  const stickToBottom = useRef(true);
  const hasLoadedInitialMessages = useRef(false);
  const requestInFlight = useRef(false);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setRefreshTick(0);
    hasLoadedInitialMessages.current = false;
    requestInFlight.current = false;
    stickToBottom.current = true;

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    if (!stickToBottom.current) return undefined;
    const frame = window.requestAnimationFrame(() => scrollToBottom());
    return () => window.cancelAnimationFrame(frame);
  }, [messagesList]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (requestInFlight.current) return;
      setRefreshTick(current => current + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const isInitialLoad = !hasLoadedInitialMessages.current;
    const shouldShowLoading = isInitialLoad || pageNumber > 1;
    if (shouldShowLoading) setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (requestInFlight.current) return;
        requestInFlight.current = true;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            hasLoadedInitialMessages.current = true;
            if (shouldShowLoading) setLoading(false);
          }

          if (pageNumber === 1 && refreshTick === 0 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          if (shouldShowLoading) setLoading(false);
          if (isInitialLoad) toastError(err);
        } finally {
          requestInFlight.current = false;
        }
      };
      fetchMessages();
    }, 0);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, refreshTick]);

  useEffect(() => {
    const handleLocalMessage = event => {
      const detail = event.detail || {};
      if (String(detail.ticketId) !== String(ticketId) || !detail.message) return;
      dispatch({ type: "ADD_MESSAGE", payload: detail.message });
      window.setTimeout(scrollToBottom, 0);
    };
    window.addEventListener("crm:message-created", handleLocalMessage);
    return () => window.removeEventListener("crm:message-created", handleLocalMessage);
  }, [ticketId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (messagesScrollRef.current) {
      messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
      stickToBottom.current = true;
    }
  };

  const handleScroll = (e) => {
    const list = e.currentTarget;
    stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 90;
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video className={classes.messageMedia} controls preload="metadata" playsInline>
          <source src={message.mediaUrl} type={message.mimeType || "video/mp4"} />
          Tu navegador no puede reproducir este video.
        </video>
      );
    } else {
      return (
        <div className={classes.downloadMedia}>
          <a className={classes.documentCard} target="_blank" rel="noopener noreferrer" href={message.mediaUrl}>
            <InsertDriveFileOutlined className={classes.documentIcon} />
            <span className={classes.documentMeta}>
              <span className={classes.documentName}>{message.body || "Archivo adjunto"}</span>
              <span className={classes.documentSize}>{message.mediaType ? String(message.mediaType).toUpperCase() : "DOCUMENTO"}{message.fileSize ? ` · ${message.fileSize}` : ""}</span>
            </span>
            <GetApp color="primary" />
          </a>
        </div>
      );
    }
  };

  const renderMessageAck = (message) => {
    const ack = message.ack ?? (message.read ? 3 : 1);
    if (ack === -1) return <Block titleAccess="No se pudo enviar" fontSize="small" className={classes.ackIcons} />;
    if (ack === 0) {
      return <AccessTime titleAccess="Enviando" fontSize="small" className={classes.ackIcons} />;
    }
    if (ack === 1) {
      return <Done titleAccess="Enviado" fontSize="small" className={classes.ackIcons} />;
    }
    if (ack === 2) {
      return <DoneAll titleAccess="Entregado" fontSize="small" className={classes.ackIcons} />;
    }
    if (ack === 3 || ack === 4) {
      return <DoneAll titleAccess="Leído" fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0 || !isSameMontevideoDay(message.createdAt, messagesList[index - 1].createdAt)) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const label = isSameMontevideoDay(message.createdAt, new Date()) ? "Hoy"
        : isSameMontevideoDay(message.createdAt, yesterday) ? "Ayer" : formatMontevideoDate(message.createdAt);
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {label}
          </div>
        </span>
      );
    }
    return null;
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 8 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={clsx(classes.messageLeft, { [classes.messageGrouped]: index > 0 && !messagesList[index - 1].fromMe && isSameMontevideoDay(message.createdAt, messagesList[index - 1].createdAt) })}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {formatMontevideoTime(message.createdAt)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={clsx(classes.messageRight, { [classes.messageGrouped]: index > 0 && messagesList[index - 1].fromMe && isSameMontevideoDay(message.createdAt, messagesList[index - 1].createdAt) })}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {formatMontevideoTime(message.createdAt)}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper} style={{ ...chatCssVariables(appearance), "--chat-date-bg": chatTextColor(appearance.backgroundColor) === "#f7fafb" ? "#202c33" : "#ffffffdf", "--chat-date-text": chatTextColor(appearance.backgroundColor) === "#f7fafb" ? "#c4ced3" : "#52615b" }}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        ref={messagesScrollRef}
        className={classes.messagesList}
        style={chatSurfaceStyle(appearance)}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
        <div ref={lastMessageRef} style={{ minHeight: 1, flexShrink: 0 }} />
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
